<template>
  <div
    v-if="hasPanels && !arePanelsCollapsed"
    v-click-outside="clickOutsideConfig"
    class="panel-container"
    :class="{ 'is-resizing': isAnyPanelResizing }"
  >
    <!-- Render panels in order -->
    <transition-group name="panel-slide" tag="div" class="panels-wrapper">
      <ContentPanel
        v-for="(panel, index) in panels"
        :key="panel.id"
        :panel-id="panel.id"
        :width="panelWidths[panel.id] || panel.width"
        :left-offset="getPanelLeftOffset(index)"
        :position="index"
        :is-last="index === panels.length - 1"
        :min-width="minPanelWidth"
        :max-width="maxPanelWidth"
        @resize="handlePanelResize"
        @resize-start="handleResizeStart"
        @resize-end="handleResizeEnd"
        @close="handlePanelClose"
        @back="handlePanelBack"
      />
    </transition-group>

    <!-- Collapse Toggle Button -->
    <CollapseToggle v-if="showCollapseToggle" :is-resizing="isAnyPanelResizing" />
  </div>

  <!-- Collapsed state: only show toggle button -->
  <CollapseToggle v-else-if="hasPanels && arePanelsCollapsed" />
</template>

<script>
import { mapGetters } from 'vuex';
import ContentPanel from './ContentPanel.vue';
import CollapseToggle from './CollapseToggle.vue';

/**
 * PanelContainer Component
 *
 * Manages the multi-panel system, handling:
 * - Panel positioning based on cumulative widths
 * - Resize coordination between panels
 * - Click-outside collapse behavior
 * - Transition animations for panel open/close
 *
 * @emits panel-resize - When any panel is resized
 * @emits panel-close - When any panel is closed
 * @emits collapse - When panels are collapsed
 */
export default {
  name: 'PanelContainer',

  components: {
    ContentPanel,
    CollapseToggle,
  },

  props: {
    /**
     * Minimum width for any panel
     */
    minPanelWidth: {
      type: Number,
      default: 200,
    },

    /**
     * Maximum width for any panel
     */
    maxPanelWidth: {
      type: Number,
      default: 500,
    },

    /**
     * Whether to show the collapse toggle button
     */
    showCollapseToggle: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isAnyPanelResizing: false,
      // Local panel widths during resize (not committed to Vuex until resize-end)
      panelWidths: {},
    };
  },

  computed: {
    ...mapGetters('GENERAL', {
      panels: 'GET_PANELS',
      hasPanels: 'GET_HAS_PANELS',
      arePanelsCollapsed: 'GET_ARE_PANELS_COLLAPSED',
      railWidth: 'GET_RAIL_WIDTH',
    }),

    /**
     * Click-outside directive config
     * Excludes the rail and any dialogs
     */
    clickOutsideConfig() {
      return {
        handler: this.onClickOutside,
        include: () => {
          // Include rail, dialogs, and menus in the "safe" zone
          const elements = [
            document.querySelector('.expandable-rail'),
            document.querySelector('.v-dialog'),
            document.querySelector('.v-menu__content'),
            document.querySelector('.collapse-toggle-btn'),
          ].filter(Boolean);
          return elements;
        },
      };
    },

    /**
     * Total width of all panels (for layout calculations)
     */
    totalPanelsWidth() {
      return this.panels.reduce((sum, panel) => {
        return sum + (this.panelWidths[panel.id] || panel.width);
      }, 0);
    },
  },

  watch: {
    /**
     * Sync local widths when panels change
     */
    panels: {
      handler(newPanels) {
        // Initialize local widths for new panels
        newPanels.forEach((panel) => {
          if (!(panel.id in this.panelWidths)) {
            this.$set(this.panelWidths, panel.id, panel.width);
          }
        });

        // Clean up removed panels
        Object.keys(this.panelWidths).forEach((id) => {
          if (!newPanels.some((p) => p.id === id)) {
            this.$delete(this.panelWidths, id);
          }
        });
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    /**
     * Calculate left offset for a panel at given index
     * @param {Number} index - Panel index in the array
     * @returns {Number} - Left offset in pixels
     */
    getPanelLeftOffset(index) {
      let offset = 0;
      for (let i = 0; i < index; i++) {
        const panel = this.panels[i];
        offset += this.panelWidths[panel.id] || panel.width;
      }
      return offset;
    },

    /**
     * Handle panel resize event (during drag)
     * Updates local state only - doesn't commit to Vuex
     */
    handlePanelResize({ panelId, width }) {
      this.$set(this.panelWidths, panelId, width);
      this.$emit('panel-resize', { panelId, width });
    },

    /**
     * Handle resize start
     */
    handleResizeStart(panelId) {
      this.isAnyPanelResizing = true;
      this.$emit('resize-start', panelId);
    },

    /**
     * Handle resize end
     * Width is already committed to Vuex by ContentPanel
     */
    handleResizeEnd(panelId) {
      this.isAnyPanelResizing = false;
      this.$emit('resize-end', panelId);
    },

    /**
     * Handle panel close
     */
    handlePanelClose(panelId) {
      this.$emit('panel-close', panelId);
    },

    /**
     * Handle panel back navigation
     */
    handlePanelBack(panelId) {
      this.$emit('panel-back', panelId);
    },

    /**
     * Handle click outside
     * Collapses only the rail (icon stripe returns to mini mode)
     * Panels remain visible (always pinned behavior)
     */
    onClickOutside() {
      // Only collapse the rail - panels stay visible
      this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
      this.$emit('click-outside');
    },
  },
};
</script>

<style lang="scss" scoped>
.panel-container {
  position: fixed;
  top: var(--ds-nav-top-bar-height);
  left: 0;
  height: calc(100vh - var(--ds-nav-top-bar-height));
  pointer-events: none; // Allow clicks to pass through to main content
  z-index: 6;

  // Disable transitions during resize for performance
  &.is-resizing {
    * {
      transition: none !important;
    }
  }
}

.panels-wrapper {
  position: relative;
  height: 100%;
  pointer-events: auto; // Re-enable pointer events for panels
}

// Panel slide transition
.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: transform var(--ds-transition-collapse), opacity var(--ds-transition-collapse);
}

.panel-slide-enter,
.panel-slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.panel-slide-move {
  transition: transform var(--ds-transition-panel);
}
</style>
